import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ArrowRight, Phone, MessageSquare, Home as HomeIcon, Building2, Star,
  CalendarCheck, ShieldCheck, Sparkles, MessageCircle, SlidersHorizontal, BadgeCheck,
} from "lucide-react";
import { Reveal, MaskLine } from "../lib/motion";
import Seo from "../components/Seo";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES, CONTACT } from "../data";
import { track } from "../lib/track";

const GoogleG = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className}><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
);

function Hero() {
  const { t } = useLang();
  return (
    <section data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden bg-ink-950">
      <img src={IMAGES.heroInterior} alt={t("Clean, bright modern interior")} className="absolute inset-0 w-full h-full object-cover opacity-40" loading="eager" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink-950/75 via-ink-950/65 to-ink-950" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-16 w-full">
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.04] max-w-4xl" style={{ fontWeight: 500 }}>
          <MaskLine delay={0.1} className="text-gradient">{t("A Cleaner Home. A Better Workday.")}</MaskLine>
          <MaskLine delay={0.25} className="text-gradient">{t("One Less Thing To Worry About.")}</MaskLine>
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="mt-7 text-white/65 text-lg md:text-xl leading-relaxed max-w-2xl font-300">
          {t("Professional cleaning services for homes and businesses across Greater Montreal. Reliable service, clear communication, and a cleaner space you can count on.")}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
          className="mt-9 flex flex-col sm:flex-row gap-3">
          <Link to="/quote" data-testid="hero-primary-cta" onClick={() => track("cta_click", { id: "hero_quote" })}
            className="btn-pill group bg-white text-ink-950 pl-7 pr-6 py-4 text-sm font-600 flex items-center justify-center gap-2 hover:shadow-xl hover:shadow-white/20">
            {t("Get My Free Quote")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/commercial" data-testid="hero-secondary-cta" onClick={() => track("cta_click", { id: "hero_commercial" })}
            className="btn-pill glass text-white px-7 py-4 text-sm font-600 flex items-center justify-center hover:bg-white/10">
            {t("Commercial Services")}
          </Link>
          <a href={CONTACT.phoneHref} data-testid="hero-call-cta" onClick={() => track("phone_click", { where: "hero" })}
            className="btn-pill glass text-white px-7 py-4 text-sm font-600 flex items-center justify-center gap-2 hover:bg-white/10">
            <Phone className="h-4 w-4 text-steel-light" /> {t("Call / Text")} {CONTACT.phone}
          </a>
        </motion.div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="mt-6 text-white/40 text-sm">
          {t("Serving Montreal and the Greater Montreal Area · Free estimates · Insured")}
        </motion.p>
      </div>
    </section>
  );
}

function Segmentation() {
  const { t } = useLang();
  const paths = [
    {
      testid: "path-residential", to: "/residential", icon: HomeIcon, img: IMAGES.lobby3,
      title: "Home Cleaning", tagline: "More time for life. Less time cleaning.",
      desc: "Residential cleaning designed for busy households that want a consistently clean home without spending their free time maintaining it.",
      items: ["Recurring Cleaning", "Deep Cleaning", "One-Time Cleaning", "Move-In / Move-Out Cleaning", "Airbnb / Short-Term Rental Cleaning"],
      cta: "Get My Home Cleaning Quote", ctaTo: "/quote",
    },
    {
      testid: "path-commercial", to: "/commercial", icon: Building2, img: IMAGES.office,
      title: "Business & Facility Cleaning", tagline: "A cleaner workplace without the management headache.",
      desc: "Professional cleaning programs for businesses, offices, clinics, retail spaces, property managers and facilities.",
      items: ["Office Cleaning", "Commercial Cleaning", "Medical / Clinic Cleaning", "Retail Cleaning", "Industrial Cleaning", "Property Management", "Post-Construction Cleaning"],
      cta: "Request a Commercial Quote", ctaTo: "/commercial",
    },
  ];
  return (
    <section data-testid="segmentation-section" className="bg-ink-950 py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal><p className="overline text-steel-light mb-4 text-center">{t("Two Ways We Help")}</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-serif text-gradient text-3xl md:text-5xl text-center mb-14" style={{ fontWeight: 500 }}>{t("Cleaning That Fits Your Life.")}</h2></Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {paths.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12}>
              <div data-testid={p.testid} className={`relative overflow-hidden rounded-4xl border h-full flex flex-col ${i === 0 ? "border-steel-light/25 bg-steel/[0.07]" : "border-white/10 bg-white/[0.03]"}`}>
                <div className="relative h-44 md:h-52 overflow-hidden">
                  <img src={p.img} alt={t(p.title)} className="w-full h-full object-cover opacity-60" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-950 to-transparent" />
                  <div className="absolute bottom-4 left-6 h-11 w-11 rounded-2xl glass flex items-center justify-center"><p.icon className="h-5 w-5 text-steel-light" /></div>
                </div>
                <div className="p-7 md:p-9 flex flex-col flex-1">
                  <h3 className="font-serif text-2xl md:text-3xl text-white" style={{ fontWeight: 500 }}>{t(p.title)}</h3>
                  <p className="mt-1.5 text-steel-light font-600 text-sm">{t(p.tagline)}</p>
                  <p className="mt-3 text-white/55 text-sm leading-relaxed">{t(p.desc)}</p>
                  <ul className="mt-5 space-y-2 flex-1">
                    {p.items.map((s) => (
                      <li key={s} className="flex items-center gap-2.5 text-sm text-white/70">
                        <span className="h-1.5 w-1.5 rounded-full bg-steel-light shrink-0" /> {t(s)}
                      </li>
                    ))}
                  </ul>
                  <Link to={p.ctaTo} onClick={() => track("cta_click", { id: p.testid })}
                    className="btn-pill group mt-7 bg-white text-ink-950 px-6 py-3.5 text-sm font-600 inline-flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-white/20">
                    {t(p.cta)} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function BrandStory() {
  const { t } = useLang();
  return (
    <section data-testid="brand-story-section" className="bg-ink-900 py-24 md:py-36">
      <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
        <Reveal><h2 className="font-serif text-gradient text-3xl md:text-5xl leading-[1.1]" style={{ fontWeight: 500 }}>{t("We don't just clean. We give you time back.")}</h2></Reveal>
        <Reveal delay={0.12}>
          <p className="mt-7 text-white/60 text-lg leading-relaxed max-w-3xl mx-auto">
            {t("Life is busy enough. Work, family, responsibilities and everything in between. North Peak was built around a simple idea: you shouldn't have to spend your valuable time worrying about whether your space is clean.")}
          </p>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-4 text-white/60 text-lg leading-relaxed max-w-3xl mx-auto">
            {t("We take that responsibility off your plate with dependable, professional cleaning you can count on.")}
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <p className="mt-10 font-serif text-steel-light text-xl md:text-2xl tracking-wide" style={{ fontWeight: 500 }}>
            {t("You live your life. We'll handle the clean.")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

const WHY_BLOCKS = [
  { icon: CalendarCheck, title: "Reliable", desc: "We show up when we're scheduled and communicate clearly." },
  { icon: BadgeCheck, title: "Professional", desc: "Consistent processes and professional cleaning standards." },
  { icon: ShieldCheck, title: "Insured", desc: "Professional protection for your peace of mind." },
  { icon: Sparkles, title: "Detail-Focused", desc: "We focus on the details that make a space feel truly clean." },
  { icon: MessageCircle, title: "Clear Communication", desc: "Easy communication from quote to completed service." },
  { icon: SlidersHorizontal, title: "Flexible", desc: "Cleaning plans designed around your home, business and schedule." },
];

function WhyNorthPeak() {
  const { t } = useLang();
  return (
    <section data-testid="why-section" className="bg-ink-950 py-24 md:py-32 relative overflow-hidden glow">
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <Reveal><p className="overline text-steel-light mb-4 text-center">{t("Why North Peak")}</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-serif text-gradient text-3xl md:text-5xl text-center mb-14" style={{ fontWeight: 500 }}>{t("Built on standards, not slogans.")}</h2></Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {WHY_BLOCKS.map((b, i) => (
            <Reveal key={b.title} delay={(i % 3) * 0.08}>
              <div data-testid={`why-block-${i}`} className="glass card-hover rounded-3xl p-7 md:p-8 h-full group">
                <div className="h-11 w-11 rounded-2xl bg-white/5 flex items-center justify-center group-hover:bg-steel/20 transition-colors">
                  <b.icon className="h-5 w-5 text-steel-light" strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-600 text-white text-lg">{t(b.title)}</h3>
                <p className="mt-2 text-white/50 text-sm leading-relaxed">{t(b.desc)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useLang();
  const steps = [
    ["Request Your Quote", "Tell us about your space and what you need."],
    ["We Assess Your Needs", "We review the size, condition, frequency and requirements."],
    ["Get Your Cleaning Plan", "Receive clear pricing and a service plan built around your needs."],
    ["We Handle the Cleaning", "You get a cleaner space without having to worry about the work."],
  ];
  return (
    <section data-testid="process-section" className="bg-ink-900 py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal><p className="overline text-steel-light mb-4 text-center">{t("How It Works")}</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-serif text-gradient text-3xl md:text-5xl text-center mb-14" style={{ fontWeight: 500 }}>{t("Getting started is simple.")}</h2></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map(([title, desc], i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div data-testid={`step-${i + 1}`} className="glass card-hover rounded-3xl p-7 h-full">
                <div className="font-serif text-5xl text-steel/40" style={{ fontWeight: 400 }}>{i + 1}</div>
                <h3 className="mt-4 font-600 text-white">{t(title)}</h3>
                <p className="mt-2 text-white/50 text-sm leading-relaxed">{t(desc)}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3}>
          <div className="text-center mt-12">
            <Link to="/quote" data-testid="process-cta" onClick={() => track("cta_click", { id: "how_it_works" })}
              className="btn-pill group inline-flex bg-white text-ink-950 pl-8 pr-6 py-4 text-sm font-600 items-center gap-2 hover:shadow-xl hover:shadow-white/20">
              {t("Get My Free Quote")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TrustSection() {
  const { t } = useLang();
  const points = ["Professional cleaning process", "Clear communication", "Free estimates", "Detailed service approach", "Residential & commercial capability", "Greater Montreal service area"];
  return (
    <section data-testid="trust-proof-section" className="bg-ink-950 py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="glass rounded-4xl p-8 md:p-14 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-steel/15 blur-3xl" />
          <div className="relative grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <Reveal><p className="overline text-steel-light mb-4">{t("Our Commitment")}</p></Reveal>
              <Reveal delay={0.08}><h2 className="font-serif text-gradient text-3xl md:text-4xl leading-[1.12]" style={{ fontWeight: 500 }}>{t("Professional standards you can count on.")}</h2></Reveal>
              <div className="mt-7 grid sm:grid-cols-2 gap-3">
                {points.map((pt) => (
                  <div key={pt} className="flex items-center gap-2.5 text-sm text-white/70">
                    <Star className="h-3.5 w-3.5 text-steel-light shrink-0" /> {t(pt)}
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-5">
              <Reveal delay={0.15}>
                <div data-testid="google-profile-card" className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 text-center">
                  <GoogleG className="h-9 w-9 mx-auto mb-4" />
                  <p className="text-white font-600 mb-1.5">{t("We're on Google")}</p>
                  <p className="text-white/50 text-sm leading-relaxed mb-5">{t("See our business profile — and as we complete more jobs, real customer reviews will appear right here.")}</p>
                  <a href={CONTACT.googleUrl} target="_blank" rel="noopener noreferrer" data-testid="google-profile-link"
                    onClick={() => track("cta_click", { id: "google_profile_trust" })}
                    className="btn-pill inline-flex glass text-white px-6 py-3 text-sm font-600 hover:bg-white/10">
                    {t("View our Google Business Profile")}
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Founder() {
  const { t } = useLang();
  return (
    <section data-testid="founder-section" className="bg-ink-900 py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-4">
          <Reveal>
            <div data-testid="founder-photo-placeholder" className="aspect-[4/5] rounded-4xl border border-white/10 bg-white/[0.03] flex flex-col items-center justify-center gap-3">
              <div className="h-20 w-20 rounded-full bg-steel/15 flex items-center justify-center">
                <span className="font-serif text-3xl text-steel-light" style={{ fontWeight: 500 }}>IA</span>
              </div>
              <p className="text-white/35 text-xs">{t("Founder photo coming soon")}</p>
            </div>
          </Reveal>
        </div>
        <div className="md:col-span-8">
          <Reveal><p className="overline text-steel-light mb-4">{t("Meet the Founder")}</p></Reveal>
          <Reveal delay={0.08}><h2 className="font-serif text-gradient text-3xl md:text-4xl leading-[1.12]" style={{ fontWeight: 500 }}>{t("Built with a simple purpose.")}</h2></Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-white/60 leading-relaxed text-lg">
              {t("North Peak Facility Services was created around a simple idea: people shouldn't have to choose between having a clean space and having time for the things that matter.")}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-4 text-white/60 leading-relaxed text-lg">
              {t("We're building North Peak around professional standards, dependable service and genuine care for every space we enter.")}
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-6 text-white font-600">Isaac Asibuo Jr. <span className="text-white/40 font-400">· {t("Founder")}</span></p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link to="/about" data-testid="founder-cta" onClick={() => track("cta_click", { id: "meet_north_peak" })}
              className="group inline-flex items-center gap-2 mt-7 text-white font-600 text-sm">
              {t("Meet North Peak")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { t } = useLang();
  return (
    <section data-testid="cta-section" className="bg-ink-950 py-24 md:py-32 px-4">
      <div className="relative max-w-6xl mx-auto rounded-4xl overflow-hidden">
        <img src={IMAGES.nightBuilding} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-deep/90 to-ink-950/90" />
        <div className="relative z-10 py-20 md:py-28 px-6 text-center">
          <Reveal><h2 className="font-serif text-gradient text-3xl md:text-5xl lg:text-6xl leading-[1.08]" style={{ fontWeight: 500 }}>{t("Ready for one less thing to worry about?")}</h2></Reveal>
          <Reveal delay={0.12}><p className="mt-6 text-white/65 text-lg max-w-xl mx-auto">{t("Tell us about your space and we'll help you find the right cleaning solution.")}</p></Reveal>
          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/quote" data-testid="cta-quote-btn" onClick={() => track("cta_click", { id: "final_quote" })}
                className="btn-pill group bg-white text-ink-950 pl-8 pr-6 py-4 text-sm font-600 flex items-center justify-center gap-2 hover:shadow-xl hover:shadow-white/20">
                {t("Get My Free Quote")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href={CONTACT.smsHref} data-testid="cta-text-btn" onClick={() => track("text_click", { where: "final_cta" })}
                className="btn-pill glass text-white px-8 py-4 text-sm font-600 flex items-center justify-center gap-2 hover:bg-white/10">
                <MessageSquare className="h-4 w-4 text-steel-light" /> {t("Call / Text")} {CONTACT.phone}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.28}>
            <a href={CONTACT.googleUrl} target="_blank" rel="noopener noreferrer" data-testid="google-profile-link-footer"
              onClick={() => track("cta_click", { id: "google_profile_cta" })}
              className="mt-7 inline-flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors">
              <GoogleG className="h-4 w-4" /> {t("View us on Google")}
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-ink-950">
      <Seo path="/" description="Professional residential and commercial cleaning services across Greater Montreal. Reliable service, clear communication, free estimates. Get your free quote today." keywords="Cleaning Services Montreal, House Cleaning Montreal, Commercial Cleaning Montreal, Office Cleaning Montreal, Cleaning Services Greater Montreal" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "LocalBusiness",
          name: "North Peak Facility Services",
          description: "Professional residential and commercial cleaning services across Greater Montreal.",
          url: "https://northpeakfs.ca", telephone: "+1-438-946-0924", email: "info@northpeakfs.ca",
          areaServed: "Greater Montreal, Quebec, Canada",
          address: { "@type": "PostalAddress", addressLocality: "Montreal", addressRegion: "QC", addressCountry: "CA" },
          openingHours: "Mo-Sa 08:00-17:00",
          sameAs: ["https://share.google/vBLi2F0e9RXp859aF"],
        })}</script>
      </Helmet>
      <Hero />
      <Segmentation />
      <BrandStory />
      <WhyNorthPeak />
      <HowItWorks />
      <TrustSection />
      <Founder />
      <FinalCTA />
    </div>
  );
}
